import { FormControlLabel, MenuItem, Stack, Switch, TextField } from '@mui/material';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormDialog } from '../../../shared/components/FormDialog';
import { useCrudFormDialog } from '../../../shared/api/useCrudFormDialog';
import { impostosHooks } from './hooks';
import { impostoInputSchema, type ImpostoFormValues } from './schemas';
import { IMPOSTO_TIPOS, REGRAS_MUNICIPAIS, type ImpostoDto, type ImpostoInput } from './types';

export interface ImpostoFormDialogProps {
  open: boolean;
  imposto: ImpostoDto | null;
  onClose: () => void;
}

const DEFAULT_VALUES: ImpostoFormValues = {
  tipo: 'ISS',
  regraMunicipal: null,
  aliquota: 0,
  nome: '',
  retido: false,
  empresaId: 0,
  enviarValor: false,
  enviarAliquota: false,
  ativo: true,
};

export function ImpostoFormDialog({ open, imposto, onClose }: ImpostoFormDialogProps): JSX.Element {
  const { t } = useTranslation();
  const { form, isEdit, onSubmit, serverError } = useCrudFormDialog<
    ImpostoFormValues,
    ImpostoDto,
    ImpostoInput,
    number
  >({
    open,
    entity: imposto,
    schema: impostoInputSchema,
    defaultValues: DEFAULT_VALUES,
    fromEntity: (i) => ({
      tipo: i.tipo,
      regraMunicipal: i.regraMunicipal,
      aliquota: i.aliquota,
      nome: i.nome,
      retido: i.retido,
      empresaId: i.empresaId,
      enviarValor: i.enviarValor,
      enviarAliquota: i.enviarAliquota,
      ativo: i.ativo,
    }),
    toInput: (v) => ({ ...v, regraMunicipal: v.regraMunicipal ?? null }),
    getId: (i) => i.id,
    hooks: { useCreate: impostosHooks.useCreate, useUpdate: impostosHooks.useUpdate },
    messages: {
      created: t('features.impostos.toasts.created'),
      updated: t('features.impostos.toasts.updated'),
    },
    focusField: 'nome',
    onClose,
  });

  return (
    <FormDialog
      open={open}
      title={isEdit ? t('features.impostos.editTitle') : t('features.impostos.createTitle')}
      form={form}
      onSubmit={onSubmit}
      onCancel={onClose}
      serverError={serverError}
      maxWidth="md"
    >
      <Stack spacing={2}>
        <TextField
          label={t('features.impostos.fields.nome')}
          fullWidth
          inputProps={{ maxLength: 150 }}
          error={Boolean(form.formState.errors.nome)}
          helperText={form.formState.errors.nome?.message}
          {...form.register('nome')}
        />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <Controller
            control={form.control}
            name="tipo"
            render={({ field, fieldState }) => (
              <TextField
                select
                fullWidth
                label={t('features.impostos.fields.tipo')}
                value={field.value}
                onChange={(e) => field.onChange(e.target.value)}
                onBlur={field.onBlur}
                inputRef={field.ref}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
              >
                {IMPOSTO_TIPOS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
          <Controller
            control={form.control}
            name="aliquota"
            render={({ field, fieldState }) => (
              <TextField
                fullWidth
                type="number"
                label={t('features.impostos.fields.aliquota')}
                helperText={
                  fieldState.error?.message ?? t('features.impostos.fields.aliquotaHelper')
                }
                error={Boolean(fieldState.error)}
                inputProps={{ step: '0.01', min: 0, max: 100 }}
                value={field.value}
                onChange={(e) => field.onChange(Number(e.target.value))}
                onBlur={field.onBlur}
                inputRef={field.ref}
              />
            )}
          />
        </Stack>
        <Controller
          control={form.control}
          name="empresaId"
          render={({ field, fieldState }) => (
            <TextField
              type="number"
              fullWidth
              label={t('features.impostos.fields.empresaId')}
              helperText={
                fieldState.error?.message ?? t('features.impostos.fields.empresaIdHelper')
              }
              error={Boolean(fieldState.error)}
              value={field.value}
              onChange={(e) => field.onChange(Number(e.target.value))}
              onBlur={field.onBlur}
              inputRef={field.ref}
            />
          )}
        />
        <Controller
          control={form.control}
          name="regraMunicipal"
          render={({ field, fieldState }) => (
            <TextField
              select
              fullWidth
              label={t('features.impostos.fields.regraMunicipal')}
              helperText={
                fieldState.error?.message ?? t('features.impostos.fields.regraMunicipalHelper')
              }
              error={Boolean(fieldState.error)}
              value={field.value ?? ''}
              onChange={(e) =>
                field.onChange(
                  e.target.value === '' ? null : (e.target.value as 'MES' | 'OUT' | 'AMB'),
                )
              }
              onBlur={field.onBlur}
              inputRef={field.ref}
              SelectProps={{ displayEmpty: true }}
            >
              <MenuItem value="">
                <em>{t('features.impostos.fields.regraMunicipalNone')}</em>
              </MenuItem>
              {REGRAS_MUNICIPAIS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
        <Stack direction="row" spacing={3} flexWrap="wrap">
          {(['retido', 'enviarValor', 'enviarAliquota', 'ativo'] as const).map((name) => (
            <Controller
              key={name}
              control={form.control}
              name={name}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(field.value)}
                      onChange={(_, checked) => field.onChange(checked)}
                      onBlur={field.onBlur}
                      inputRef={field.ref}
                    />
                  }
                  label={t(`features.impostos.fields.${name}`)}
                />
              )}
            />
          ))}
        </Stack>
      </Stack>
    </FormDialog>
  );
}
